import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Info,
  Newspaper,
  Group,
  Description,
  CalendarMonth,
  MenuBook,
  Link as LinkIcon,
  Article,
  ContactMail,
  LocationOn,
  Notes,
} from "@mui/icons-material";
import { COVERAGE_COLUMNS } from "../constants";
import { coverageModuleStatusChip } from "../utils/coverageGrade";

const ICON_MAP: Record<string, React.ReactNode> = {
  Info: <Info color="secondary" />,
  Newspaper: <Newspaper color="secondary" />,
  Group: <Group color="secondary" />,
  Description: <Description color="secondary" />,
  CalendarMonth: <CalendarMonth color="secondary" />,
  MenuBook: <MenuBook color="secondary" />,
  Link: <LinkIcon color="secondary" />,
  Article: <Article color="secondary" />,
  ContactMail: <ContactMail color="secondary" />,
  LocationOn: <LocationOn color="secondary" />,
  Notes: <Notes color="secondary" />,
};

const TABLE_HEADER_SX = {
  fontWeight: 700,
  fontSize: "12px",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
};

interface StatsTableProps {
  stats: any | null;
}

export function StatsTable({ stats }: StatsTableProps) {
  const theme = useTheme();
  const coverage = stats?.coverage;
  const total = stats?.totalAlliances ?? 0;
  const recordCounts = stats?.aggregates?.recordCounts ?? {};

  if (!coverage) return null;

  if (!total) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        U registru još nema saveza - statistika će se pojaviti nakon prvog
        unosa.
      </Typography>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 2,
        maxWidth: "100%",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Table size="small" sx={{ minWidth: { xs: 520, sm: 640 } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell
              sx={{
                ...TABLE_HEADER_SX,
                fontSize: { xs: "10px", sm: "12px" },
              }}
            >
              Područje podataka
            </TableCell>
            <TableCell
              sx={{
                ...TABLE_HEADER_SX,
                fontSize: { xs: "10px", sm: "12px" },
              }}
            >
              Status
            </TableCell>
            <TableCell
              align="right"
              sx={{
                ...TABLE_HEADER_SX,
                fontSize: { xs: "10px", sm: "12px" },
              }}
            >
              Saveza
            </TableCell>
            <TableCell
              align="right"
              sx={{
                ...TABLE_HEADER_SX,
                fontSize: { xs: "10px", sm: "12px" },
              }}
            >
              Udio
            </TableCell>
            <TableCell
              align="right"
              sx={{
                ...TABLE_HEADER_SX,
                fontSize: { xs: "10px", sm: "12px" },
              }}
            >
              Stavke
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {COVERAGE_COLUMNS.map((mod) => {
            const data = coverage[mod.key];
            const percentage = data?.percentage ?? 0;
            const count = data?.count ?? 0;
            const status = coverageModuleStatusChip(percentage, theme);
            const records = recordCounts[mod.key];
            const recordsDisplay = typeof records === "number" ? records : "-";

            return (
              <TableRow key={mod.key} hover>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {ICON_MAP[mod.iconName]}
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {mod.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textTransform: "none",
                          letterSpacing: 0,
                          display: { xs: "none", sm: "block" },
                        }}
                      >
                        {mod.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={status.tooltip} enterTouchDelay={400}>
                    <Chip label={status.label} size="small" sx={status.sx} />
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      fontFamily: "'Inter', monospace",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {count} / {total}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      fontFamily: "'Inter', monospace",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {percentage}%
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      fontFamily: "'Inter', monospace",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {recordsDisplay}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
